import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

import {
  errorAlert,
  successAlert,
  warningAlert,
} from "../../../../common/components/Toasts/CustomToasts";
import CommonButton from "../../../../common/components/Buttons/CommonButton";
import CommonDynamicTableNew from "../../../../common/components/CommonTable/CommonDynamicTableNew";
import DropdownField from "../../../../common/components/FormFields/DropdownField";
import InputField from "../../../../common/components/FormFields/InputField";
import TimePickerFieldNew from "../../../../common/components/FormFields/TimePickerFieldNew";
import ConfirmationModal from "../../../../common/components/ConfirmationModal";
import { CapitalizeStatement } from "../../../../common/components/Custom Hooks/CapitalizeStatement";
import { DeleteIcon } from "../../../../assets/CommonAssets";
import { DisabledDeleteIcon } from "../../../../opd/components/common/EmrCommonAsset/Asset";
import { UseMilisecondsToDate } from "../../../../common/components/Custom Hooks/UseMilisecondsToDate";
import { saveRtFeedFeedingPlanForPatient } from "../../../services/diet/dietorder/RTFeedServices";
import TableInputField from "../../../../common/components/FormFields/TableInputField";

const defaultValues = {
  date: null,
  time: null,
  feed: "",
  frequency: null,
  qtyInMl: "",
};

const UserAction = [
  {
    id: 1,
    permission: "Create",
    isAction: false,
    menuId: null,
    functionalityId: null,
    menuKey: null,
  },
  {
    id: 2,
    permission: "Edit",
    isAction: true,
    menuId: null,
    functionalityId: null,
    menuKey: null,
  },
  {
    id: 3,
    permission: "Delete",
    isAction: true,
    menuId: null,
    functionalityId: null,
    menuKey: null,
  },
  {
    id: 4,
    permission: "Feeded",
    isAction: true,
    menuId: null,
    functionalityId: null,
    menuKey: null,
  },
];

const schema = yup.object().shape({});

function FeedingSchedule(props) {
  const {
    scheduleFeedingData,
    setScheduleFeedingData,
    deletedScheduleFedData,
    setDeletedSheduleFedData,
    setOpenDietFoodAssModal,
    setOpenBackdrop,
    setActiveTab,
    selectedRow,
  } = props;
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    register,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const [userAction, setUserAction] = useState(UserAction);
  const [editIndex, setEditIndex] = useState(null);
  const [postConfirmationModal, setPostConfirmationModal] = useState(false);
  const [finalObject, setFinalObject] = useState(null);
  const [openFeededConfirmationModal, setOpenFeededConfirmationModal] =
    useState(false);

  /* ---------------- UTILITIES ---------------- */
  const getEndOfDay = () => {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight;
  };

  const generateTimeSlots = (startTime, frequencyHours) => {
    const slots = [];
    const endOfDay = getEndOfDay();
    let currentSlotTime = new Date(startTime);

    while (currentSlotTime <= endOfDay) {
      slots.push(new Date(currentSlotTime));
      currentSlotTime = new Date(
        currentSlotTime.getTime() + frequencyHours * 60 * 60 * 1000,
      );
    }
    return slots;
  };

  const loggedUser = JSON.parse(localStorage.getItem("userInfo"));
  // empId
  // const sortScheduleData = (data) =>
  //   [...data].sort((a, b) => {
  //     const feedCompare = a.feed
  //       .toLowerCase()
  //       .localeCompare(b.feed.toLowerCase());
  //     return feedCompare !== 0
  //       ? feedCompare
  //       : new Date(a.time) - new Date(b.time);
  //   });

  const sortScheduleData = (data) =>
    [...data].sort((a, b) => new Date(a.time) - new Date(b.time));

  /* ---------------- VALIDATION CHECKS ---------------- */
  const hasOneTimeDuplicate = (feed) =>
    scheduleFeedingData.some(
      (item, idx) =>
        (editIndex === null || idx !== editIndex) &&
        item.feed.toLowerCase() === feed.toLowerCase() &&
        item.frequency === "One Time",
    );

  const hasExactTimeConflict = (feed, time) => {
    const checkTime = new Date(time).setSeconds(0, 0);
    return scheduleFeedingData.some(
      (item, idx) =>
        (editIndex === null || idx !== editIndex) &&
        item.feed.toLowerCase() === feed.toLowerCase() &&
        new Date(item.time).setSeconds(0, 0) === checkTime,
    );
  };

  const isTooCloseToExistingSlot = (feed, time) => {
    const checkTime = new Date(time).setSeconds(0, 0);
    const minimumGapMinutes = 30;

    return scheduleFeedingData.find((item, idx) => {
      if (editIndex !== null && idx === editIndex) return false;
      if (item.feed.toLowerCase() !== feed.toLowerCase()) return false;

      const existingTime = new Date(item.time).setSeconds(0, 0);
      const diffMinutes = Math.abs((existingTime - checkTime) / (1000 * 60));
      return diffMinutes > 0 && diffMinutes < minimumGapMinutes;
    });
  };

  const isValidFrequencyAlignment = (feed, time, frequencyLabel) => {
    if (frequencyLabel === "One Time") return true;

    const frequencyHours = parseInt(frequencyLabel.split(" ")[0]);
    const frequencyMinutes = frequencyHours * 60;
    const checkTime = new Date(time).setSeconds(0, 0);

    const existingSlots = scheduleFeedingData
      .filter(
        (item, idx) =>
          item.feed.toLowerCase() === feed.toLowerCase() &&
          item.frequency === frequencyLabel &&
          (editIndex === null || idx !== editIndex),
      )
      .map((item) => new Date(item.time).setSeconds(0, 0));

    if (existingSlots.length === 0) return true;

    const baseTime = Math.min(...existingSlots);
    const diffMinutes = Math.abs((checkTime - baseTime) / (1000 * 60));
    return diffMinutes % frequencyMinutes === 0;
  };

  const isFrequencyValidTillEndOfDay = (startTime, frequencyHours) => {
    const endOfDay = getEndOfDay();
    const firstNextSlot = new Date(
      startTime.getTime() + frequencyHours * 60 * 60 * 1000,
    );
    return firstNextSlot <= endOfDay;
  };

  const hasDifferentFrequencyConflict = (feed, frequencyLabel) =>
    scheduleFeedingData.some(
      (item, idx) =>
        (editIndex === null || idx !== editIndex) &&
        item.feed.toLowerCase() === feed.toLowerCase() &&
        item.frequency !== frequencyLabel,
    );

  /* ---------------- VALIDATION ---------------- */
  const validateFormData = (formData) => {
    const { time, feed, frequency, qtyInMl } = formData;
    let hasError = false;

    if (!time) {
      setError("time", { type: "manual", message: "Time is required" });
      hasError = true;
    }
    if (!feed?.trim()) {
      setError("feed", { type: "manual", message: "Feed name is required" });
      hasError = true;
    }
    if (!frequency) {
      setError("frequency", {
        type: "manual",
        message: "Frequency is required",
      });
      hasError = true;
    }
    if (!qtyInMl) {
      setError("qtyInMl", { type: "manual", message: "Quantity is required" });
      hasError = true;
    }

    if (hasError) {
      warningAlert("Please fill all required fields");
      return false;
    }

    const trimmedFeed = feed.trim();

    if (trimmedFeed.length < 2) {
      setError("feed", {
        type: "manual",
        message: "Feed name must be at least 2 characters",
      });
      warningAlert("Feed name must be at least 2 characters");
      return false;
    }

    const quantity = Number(qtyInMl);
    if (isNaN(quantity) || quantity <= 0) {
      setError("qtyInMl", {
        type: "manual",
        message: "Quantity must be a positive number",
      });
      warningAlert("Quantity must be a positive number");
      return false;
    }
    // if (quantity > 5000) {
    //   setError("qtyInMl", {
    //     type: "manual",
    //     message: "Quantity cannot exceed 5000 ML",
    //   });
    //   warningAlert("Quantity cannot exceed 5000 ML");
    //   return false;
    // }

    const selectedTime = new Date(time).setSeconds(0, 0);
    const currentTime = new Date().setSeconds(0, 0);

    if (selectedTime < currentTime) {
      setError("time", {
        type: "manual",
        message: "Cannot select a past time",
      });
      warningAlert("Cannot select a time in the past");
      return false;
    }

    if (hasDifferentFrequencyConflict(trimmedFeed, frequency?.label)) {
      setError("feed", { type: "manual", message: "Feed already exists" });
      setError("frequency", {
        type: "manual",
        message: "Different frequency already exists",
      });
      warningAlert(
        `"${trimmedFeed}" is already scheduled with a different frequency.`,
      );
      return false;
    }

    if (!isValidFrequencyAlignment(trimmedFeed, time, frequency?.label)) {
      setError("time", {
        type: "manual",
        message: "Time does not align with feed frequency",
      });
      warningAlert(
        `Invalid time for "${trimmedFeed}". Please align with ${frequency?.label} schedule.`,
      );
      return false;
    }

    return true;
  };

  const validateFrequencyRules = (formData) => {
    const { time, feed, frequency } = formData;
    const frequencyLabel = frequency?.label;
    const trimmedFeed = feed.trim();

    if (hasExactTimeConflict(trimmedFeed, time)) {
      warningAlert(
        `Time slot already exists!\n\n"${trimmedFeed}" is already scheduled at this exact time.`,
      );
      return false;
    }

    const tooCloseSlot = isTooCloseToExistingSlot(trimmedFeed, time);

    if (frequencyLabel !== "One Time") {
      const frequencyHours = parseInt(frequencyLabel.split(" ")[0]);
      const selectedTime = new Date(time);

      if (
        editIndex === null &&
        !isFrequencyValidTillEndOfDay(selectedTime, frequencyHours)
      ) {
        warningAlert(
          `Invalid feeding plan!\n\nThis schedule does not allow another feed before 12:00 AM.\nPlease choose a smaller frequency or earlier time.`,
        );
        return false;
      }
    }

    if (tooCloseSlot) {
      warningAlert(
        `Time slots too close!\n\n"${trimmedFeed}" already has a slot at ${tooCloseSlot.time}.`,
      );
      return false;
    }

    if (frequencyLabel === "One Time" && hasOneTimeDuplicate(trimmedFeed)) {
      warningAlert(
        `"${trimmedFeed}" with frequency "One Time" already exists.`,
      );
      return false;
    }

    if (frequencyLabel !== "One Time" && new Date(time) > getEndOfDay()) {
      warningAlert(
        "Selected time is beyond allowed range. Please select a time up to 12:00 AM.",
      );
      return false;
    }

    return true;
  };

  /* ---------------- ADD / UPDATE ---------------- */
  const handleAddOrUpdate = () => {
    const formData = getValues();

    if (!validateFormData(formData) || !validateFrequencyRules(formData))
      return;

    const { time, feed, frequency, qtyInMl } = formData;
    const frequencyLabel = frequency?.label;
    const trimmedFeed = feed.trim();

    // EDIT MODE: Only update the specific row, no iteration
    if (editIndex !== null) {
      const payload = {
        time,
        feed: trimmedFeed,
        frequency: frequencyLabel,
        frequencyId: frequency?.id,
        "qty(Ml)": CapitalizeStatement(qtyInMl),
        isDelete: false,
      };

      // Update only the edited row
      const updatedData = scheduleFeedingData.map((item, idx) =>
        idx === editIndex ? payload : item,
      );

      setScheduleFeedingData(sortScheduleData(updatedData));
      setEditIndex(null);
      reset(defaultValues);
      return;
    }

    // ADD MODE: Generate slots based on frequency
    if (frequencyLabel === "One Time") {
      const payload = {
        time,
        feed: trimmedFeed,
        frequency: frequencyLabel,
        frequencyId: frequency?.id,
        "qty(Ml)": CapitalizeStatement(qtyInMl),
        isDelete: false,
      };

      const updatedData = [...scheduleFeedingData, payload];
      setScheduleFeedingData(sortScheduleData(updatedData));
    } else {
      const frequencyHours = parseInt(frequencyLabel.split(" ")[0]);
      const timeSlots = generateTimeSlots(new Date(time), frequencyHours);

      const newSlots = timeSlots.filter(
        (slotTime) =>
          !hasExactTimeConflict(trimmedFeed, slotTime) &&
          !isTooCloseToExistingSlot(trimmedFeed, slotTime),
      );

      if (newSlots.length === 0) {
        warningAlert("No available time slots.");
        return;
      }

      const newEntries = newSlots.map((slotTime) => ({
        time: slotTime.getHours() === 0 ? getEndOfDay() : slotTime,
        feed: trimmedFeed,
        frequency: frequencyLabel,
        frequencyId: frequency?.id,
        "qty(Ml)": CapitalizeStatement(qtyInMl),
        isDelete: false,
      }));

      const updatedData = [...scheduleFeedingData, ...newEntries];
      setScheduleFeedingData(sortScheduleData(updatedData));
    }

    reset(defaultValues);
  };

  console.log("scheduleFeedingData", scheduleFeedingData);

  const hasPermission = (type) =>
    Array.isArray(userAction) &&
    userAction.some(
      (option) => option?.isAction === true && option?.permission === type,
    );

  const renderInput = (row, index, header) => {
    return (
      <>
        {header === "time" && (
          <p>
            {row?.time?.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </p>
        )}
        {header === "feed" && (
          <div className="my-1">
            {hasPermission("Edit") ? (
              <TableInputField
                control={control}
                name={`feed${index}`}
                defaultValue={row?.["feed"]}
                error={errors?.[`feed${index}`]}
                sx={{ maxWidth: 250 }}
                inputRef={{
                  ...register(`feed${index}`, {
                    onChange: (e) => {
                      const value = e.target.value?.trim();
                      setScheduleFeedingData((prev) =>
                        prev.map((item, idx) =>
                          idx === index ? { ...item, feed: value } : item,
                        ),
                      );
                    },
                  }),
                }}
              />
            ) : (
              <p>{row?.feed}</p>
            )}
          </div>
        )}
        {header === "qty(Ml)" && (
          <div className="my-1">
            {hasPermission("Edit") ? (
              <TableInputField
                control={control}
                name={`qty${index}`}
                type={"number"}
                defaultValue={row?.["qty(Ml)"]}
                error={errors?.[`qty${index}`]}
                sx={{ maxWidth: 150 }}
                inputRef={{
                  ...register(`qty${index}`, {
                    onChange: (e) => {
                      const value = e.target.value?.trim();
                      setScheduleFeedingData((prev) =>
                        prev.map((item, idx) =>
                          // item.Id === row.Id
                          idx === index ? { ...item, "qty(Ml)": value } : item,
                        ),
                      );
                    },
                  }),
                }}
              />
            ) : (
              <p>{row?.["qty(Ml)"]}</p>
            )}
          </div>
        )}
      </>
    );
  };

  console.log(
    "deleted",
    deletedScheduleFedData,
    "shedule Data",
    scheduleFeedingData,
  );
  const renderActions = (row, index) => (
    <div className="flex gap-x-1 items-center">
      {userAction?.map((option) => {
        return (
          <>
            {/* {option.isAction === true && option.permission === "Edit" ? (
              !row?.isEdit ? (
                !row?.isFeeded ? (
                  <EditIcon
                    title={"Edit"}
                    type={"button"}
                    onClick={() => {
                      setScheduleFeedingData((prevData) =>
                        prevData?.map((data, idx) =>
                          idx === index ? { ...data, isEdit: true } : data,
                        ),
                      );
                      // handleEdit(row, index);
                    }}
                  />
                ) : (
                  <DisabledEditIcon key={option.id} type="button" />
                )
              ) : !row?.isFeeded ? (
                <UpdateIcon
                  type={"button"}
                  title={"update"}
                  onClick={() => {
                    if (row?.feed?.trim() == "" || row?.feed == undefined) {
                      setError(`feed${index}`, {
                        type: "manual",
                        message: "field should be nkot empty",
                      });
                      return;
                    }
                    if (row?.qty?.trim() == "" || row?.qty == undefined) {
                      setError(`qty${index}`, {
                        type: "manual",
                        message: "field should be nkot empty",
                      });
                      return;
                    }
                    setScheduleFeedingData((prevData) =>
                      prevData?.map((data, idx) =>
                        idx === index ? { ...data, isEdit: false } : data,
                      ),
                    );
                    // handleEdit(row, index);
                  }}
                />
              ) : (
                <DisableUpdateIcon key={option.id} type="button" />
              )
            ) : null} */}
            {option.isAction === true && option.permission === "Delete" ? (
              !row?.isFeeded ? (
                <DeleteIcon
                  key={option.id}
                  title="Delete"
                  type="button"
                  onClick={() => {
                    setScheduleFeedingData((prev) => {
                      const updated = [...prev];
                      const deletedRow = updated[index];
                      updated.splice(index, 1);

                      // Push into deleted list
                      setDeletedSheduleFedData((prevDeleted) => [
                        ...prevDeleted,
                        { ...deletedRow, isDelete: true },
                      ]);

                      return updated;
                    });
                  }}
                />
              ) : (
                <DisabledDeleteIcon key={option.id} type="button" />
              )
            ) : null}
            {/* {option.isAction === true &&
            option.permission === "Feeded" &&
            row?.id ? (
              <fieldset
                className="ml-3"
                onChange={(e) => {
                  // setOpenFeededConfirmationModal(true);
                  const checked = e.target.checked;
                  setScheduleFeedingData((prevData) =>
                    prevData.map((schedule, idx) =>
                      // schedule?.id === row?.id
                      idx === index
                        ? { ...schedule, isFeeded: checked }
                        : schedule,
                    ),
                  );
                }}
              >
                <CheckBoxField
                  style={{ padding: 0, margin: 0 }}
                  control={control}
                  name={`isFeeded${index}`}
                  defaultValue={row?.isFeeded ? true : false}
                />
              </fieldset>
            ) : null} */}
          </>
        );
      })}
    </div>
  );
  const handleMarkPatientFeeded = () => {
    // setOpenBackdrop(true);
    // deleteVisaTypeById(selectedRow?.Id)
    //   .then((response) => response.data)
    //   .then((res) => {
    //     if (res.statusCode === 202) {
    //       populateTable();
    //       setOpenBackdrop(false);
    //       setDeleteConfirmationModal(false);
    //       setSelectedRow(null);
    //       successAlert(res.message);
    //       setPage(0);
    //     }
    //   })
    //   .catch((error) => {
    //     errorAlert(error.message);
    //     setOpenBackdrop(false);
    //   });
  };
  /* ---------------- HANDLERS ---------------- */
  const handleEdit = (row, index) => {
    console.log("row", row, index);

    setEditIndex(index);
    setValue("time", new Date(row?.time));
    setValue("feed", row.feed);
    setValue("frequency", {
      id: row.frequencyId,
      label: row.frequency,
      value: row.frequencyId,
    });
    setValue("qtyInMl", row?.["qty(Ml)"]);
  };

  const handleSaveClick = () => {
    if (!scheduleFeedingData || scheduleFeedingData.length === 0) {
      warningAlert("Add at least one feeding entry");
      return;
    }

    let hasError = false;
    if (scheduleFeedingData?.length > 0) {
      scheduleFeedingData.forEach((row, index) => {
        if (!row?.feed || row.feed.trim() === "") {
          setError(`feed${index}`, {
            type: "manual",
            message: "Feed is required",
          });
          hasError = true;
        }
        if (!row?.["qty(Ml)"]) {
          setError(`qty${index}`, {
            type: "manual",
            message: "Quantity is required",
          });
          hasError = true;
        }
      });
    }
    if (hasError) return;

    let feedsArray = [];
    for (let obj of scheduleFeedingData) {
      console.log(
        "feedsArrayfeedsArrayfeedsArray",
        UseMilisecondsToDate(obj?.time, "yyyy-MM-dd'T'HH:mm:ss"),
      );
      feedsArray.push({
        feedTime: UseMilisecondsToDate(obj?.time, "yyyy-MM-dd'T'HH:mm:ss"),
        feed: CapitalizeStatement(obj?.feed),
        qty: CapitalizeStatement(obj?.["qty(Ml)"]),
        delivered: false,
      });
    }

    let postObj = {
      admissionId: selectedRow?.admissionId,
      empId: loggedUser?.empId,
      rtFeedsDtoList: feedsArray,
    };
    setFinalObject(postObj);
    setPostConfirmationModal(true);
  };

  const onSubmit = () => {
    setPostConfirmationModal(false);
    setOpenBackdrop(true);
    saveRtFeedFeedingPlanForPatient(finalObject)
      .then((response) => response?.data)
      .then((res) => {
        if (res.statusCode === 200 || res.statusCode === 201) {
          setOpenBackdrop(false);
          successAlert(res?.message);
          setPostConfirmationModal(false);
          reset(defaultValues);
          setOpenDietFoodAssModal(false);
          setScheduleFeedingData([]);
          setDeletedSheduleFedData([]);
          setFinalObject(null);
        } else {
          errorAlert(res?.message);
          setOpenBackdrop(false);
        }
      })
      .catch((error) => {
        errorAlert(error?.message);
        setOpenBackdrop(false);
      });
  };

  return (
    <div>
      <h1 className="font-bold text-lg "> RT Feed Plan</h1>
      <form
        // onKeyDown={(e) => {
        //   if (e.key === "Enter") {
        //     e.preventDefault();
        //   }
        // }}
        onSubmit={handleSubmit(handleSaveClick)}
      >
        <div className="grid grid-cols-5 gap-2 items-center mt-2">
          <TimePickerFieldNew
            control={control}
            name="time"
            label="Time *"
            minTime={new Date().setSeconds(0, 0)}
            error={errors.time}
            slotProps={{ textField: { size: "small" } }}
            disabled={editIndex !== null}
          />
          <InputField
            name="feed"
            label="Feed *"
            control={control}
            error={errors.feed}
          />
          <DropdownField
            control={control}
            name="frequency"
            placeholder="Frequency *"
            isSearchable
            dataArray={Array.from({ length: 13 }, (_, i) => ({
              id: i + 1,
              label: i === 0 ? "One Time" : `${i} Hr`,
              value: i + 1,
            }))}
            error={errors.frequency}
            isDisabled={editIndex !== null}
          />
          <InputField
            name="qtyInMl"
            label="Qty In (Ml) *"
            type="number"
            control={control}
            error={errors.qtyInMl}
          />
          {userAction?.map((option) =>
            !option.isAction && option.permission === "Create" ? (
              <CommonButton
                label={editIndex !== null ? "Update" : "Add"}
                type="button"
                className="bg-customBlue text-white font-semibold"
                onClick={handleAddOrUpdate}
              />
            ) : null,
          )}
        </div>

        <div className="w-full mt-2">
          {scheduleFeedingData.length > 0 ? (
            <CommonDynamicTableNew
              dataResult={scheduleFeedingData}
              highlightRow={false}
              tableClass="capitalize max-h-[300px]"
              renderActions={renderActions}
              renderInput={renderInput}
              editableColumns={["time", "feed", "qty(Ml)"]}
              removeHeaders={[
                "id",
                "frequencyId",
                "isFeeded",
                "frequency",
                "isEdit",
                "isDelete",
              ]}
            />
          ) : (
            <p className="py-12 text-center text-gray-600">
              No Feeds Added<span className="animate-pulse">...</span>
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <CommonButton
            type="button"
            label="Reset"
            className="resetButton text-customRed border border-customRed"
            onClick={() => {
              reset(defaultValues);
              setScheduleFeedingData([]);
            }}
          />
          <CommonButton
            type="submit"
            label="Save"
            className="saveButton bg-customGreen text-white"
          />
        </div>
      </form>

      {/* {openFeededConfirmationModal && (
        <ConfirmationModal
          confirmationOpen={openFeededConfirmationModal}
          confirmationHandleClose={() => setOpenFeededConfirmationModal(false)}
          confirmationSubmitFunc={handleMarkPatientFeeded}
          confirmationMsg="Are you sure you want to confirm this feeding?"
          confirmationButtonMsg="Confirm Feeding"
          confirmationLabel="Confirmation"
        />
      )} */}
      {postConfirmationModal && (
        <ConfirmationModal
          confirmationOpen={postConfirmationModal}
          confirmationHandleClose={() => setPostConfirmationModal(false)}
          confirmationSubmitFunc={onSubmit}
          confirmationMsg="Are you sure to save this Feeding Plan?"
          confirmationButtonMsg="Confirm"
          confirmationLabel="Confirmation"
        />
      )}
    </div>
  );
}

export default FeedingSchedule;
